import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const Lightbox = ({ isOpen, images, currentIndex, onClose, onNext, onPrev }) => {
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    
    switch(e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        onPrev();
        break;
      case 'ArrowRight':
        onNext();
        break;
      default:
        break;
    }
  }, [isOpen, onClose, onNext, onPrev]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      // Ensure overflow is reset when lightbox closes
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.96)' }}
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-7xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white hover:text-brand-gold transition-colors z-10"
          aria-label="Close lightbox"
        >
          <X size={32} />
        </button>

        {/* Main Image */}
        <div 
          className="relative flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={images[currentIndex]}
            alt={`Image ${currentIndex + 1} of ${images.length}`}
            className="w-full h-auto max-h-[85vh] object-contain"
          />

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={onPrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all shadow-lg"
                aria-label="Previous image"
              >
                <ChevronLeft size={24} className="text-brand-dark" />
              </button>
              <button
                onClick={onNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all shadow-lg"
                aria-label="Next image"
              >
                <ChevronRight size={24} className="text-brand-dark" />
              </button>
            </>
          )}

          {/* Image Counter */}
          <div 
            className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-sm backdrop-blur-sm text-white"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          >
            {currentIndex + 1} / {images.length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lightbox;
